import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Wrench, Search, X, ChevronDown, FileText, Send, Loader2 } from "lucide-react";
import { api, API_BASE, getStoredToken, formatApiError } from "../lib/api";

const SUPERVISOR_EMAIL_KEY = "rougee:last_supervisor_email";
const SUPERVISOR_NAME_KEY = "rougee:last_supervisor_name";

export default function ToolRequest() {
  const nav = useNavigate();
  const [tools, setTools] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [selected, setSelected] = useState(() => new Set());
  const [notes, setNotes] = useState("");
  const [supervisorEmail, setSupervisorEmail] = useState(() => {
    try { return localStorage.getItem(SUPERVISOR_EMAIL_KEY) || ""; } catch { return ""; }
  });
  const [supervisorName, setSupervisorName] = useState(() => {
    try { return localStorage.getItem(SUPERVISOR_NAME_KEY) || ""; } catch { return ""; }
  });
  const [submitting, setSubmitting] = useState(false);
  const [expandedCats, setExpandedCats] = useState(() => new Set());

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get("/comed-tools", { params: { active_only: true } });
        setTools(data);
        const cats = new Set(data.map((t) => t.Category || "General"));
        setExpandedCats(cats);
      } catch (e) {
        toast.error(formatApiError(e.response?.data?.detail) || "Failed to load tools");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const grouped = useMemo(() => {
    const needle = q.trim().toLowerCase();
    const map = new Map();
    for (const t of tools) {
      if (needle) {
        const hay = `${t.Tool_Name} ${t.Cat_ID} ${t.Category || ""}`.toLowerCase();
        if (!hay.includes(needle)) continue;
      }
      const cat = t.Category || "General";
      if (!map.has(cat)) map.set(cat, []);
      map.get(cat).push(t);
    }
    return [...map.entries()].sort(([a], [b]) => a.localeCompare(b));
  }, [tools, q]);

  const toggle = (id) => {
    setSelected((s) => {
      const next = new Set(s);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const toggleCat = (cat) => {
    setExpandedCats((s) => {
      const next = new Set(s);
      if (next.has(cat)) next.delete(cat); else next.add(cat);
      return next;
    });
  };

  const selectedTools = useMemo(
    () => tools.filter((t) => selected.has(t.id)),
    [tools, selected]
  );

  const canSubmit = selected.size > 0 && !submitting;

  const onGenerate = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      const payload = {
        Tool_Ids: [...selected],
        Requestor_Notes: notes.trim(),
        Supervisor_Email: supervisorEmail.trim().toLowerCase(),
        Supervisor_Name: supervisorName.trim(),
      };
      const { data } = await api.post("/tool-requests", payload);
      try {
        if (supervisorEmail.trim()) localStorage.setItem(SUPERVISOR_EMAIL_KEY, supervisorEmail.trim());
        if (supervisorName.trim()) localStorage.setItem(SUPERVISOR_NAME_KEY, supervisorName.trim());
      } catch {}
      toast.success(`Request ${data.Request_Number} submitted`);
      nav(`/tool-requests/${data.id}`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Submission failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="px-4 sm:px-6 py-5 max-w-5xl mx-auto pb-32" data-testid="tool-request-page">
      <div className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-[0.2em] text-emerald-700 mb-1">
        <Wrench className="h-3.5 w-3.5" />
        New tool request
      </div>
      <h1 className="font-display text-3xl sm:text-4xl text-slate-900 uppercase tracking-tight leading-none mb-1" data-testid="tool-request-title">
        Tool Request
      </h1>
      <p className="text-sm text-slate-600 mb-5">
        Pick the ComEd-approved tools, fill in your supervisor's name and email, and generate a formal approval letter.
      </p>

      {/* Supervisor name (typed by user) + email row */}
      <div className="rg-card p-4 grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
        <div>
          <label className="block text-[11px] font-bold uppercase tracking-wider text-slate-700 mb-1.5">
            Supervisor name
          </label>
          <input
            type="text"
            value={supervisorName}
            onChange={(e) => setSupervisorName(e.target.value)}
            data-testid="tool-request-supervisor-name"
            placeholder="e.g. Chris Alderson"
            maxLength={160}
            className="rg-tap w-full border-2 border-slate-300 rounded-sm px-3 text-base bg-white focus:border-emerald-700 outline-none"
          />
          <div className="text-[10px] text-slate-500 mt-1">
            Saved on this device for next time.
          </div>
        </div>
        <div>
          <label className="block text-[11px] font-bold uppercase tracking-wider text-slate-700 mb-1.5">
            Supervisor email (for the approval letter)
          </label>
          <input
            type="email"
            value={supervisorEmail}
            onChange={(e) => setSupervisorEmail(e.target.value)}
            data-testid="tool-request-supervisor-email"
            placeholder="supervisor@company.com"
            className="rg-tap w-full border-2 border-slate-300 rounded-sm px-3 text-base bg-white focus:border-emerald-700 outline-none"
          />
          <div className="text-[10px] text-slate-500 mt-1">
            Saved on this device. Used for the “Email letter” button.
          </div>
        </div>
      </div>

      {/* Search + summary */}
      <div className="rg-card p-3 mb-3 flex items-center gap-2">
        <Search className="h-4 w-4 text-slate-500 flex-shrink-0" />
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          data-testid="tool-request-search"
          placeholder="Search tools or Cat ID…"
          className="flex-1 min-w-0 text-base outline-none"
        />
        {q && (
          <button onClick={() => setQ("")} className="p-1 hover:bg-slate-100 rounded-sm" data-testid="tool-request-search-clear" type="button">
            <X className="h-4 w-4 text-slate-500" />
          </button>
        )}
        <span
          data-testid="tool-request-selected-count"
          className="text-xs font-bold uppercase tracking-wider bg-emerald-700 text-white px-2 py-1 rounded-sm border-2 border-emerald-900"
        >
          {selected.size} selected
        </span>
      </div>

      {/* Tool list grouped by category */}
      {loading ? (
        <div className="rg-card p-12 text-center text-slate-500 flex items-center justify-center gap-2">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading tools…
        </div>
      ) : tools.length === 0 ? (
        <div className="rg-card p-8 text-center text-slate-500" data-testid="tool-request-empty">
          <Wrench className="h-10 w-10 mx-auto text-slate-300 mb-2" />
          <div className="font-bold text-slate-700">No tools in the ComEd catalog yet.</div>
          <div className="text-xs mt-1">
            Ask an admin to seed the list from <strong>Admin → ComEd Tool Catalog</strong>.
          </div>
        </div>
      ) : (
        <div className="space-y-2">
          {grouped.map(([cat, items]) => {
            const open = expandedCats.has(cat);
            const catSelected = items.filter((t) => selected.has(t.id)).length;
            return (
              <div key={cat} className="rg-card overflow-hidden">
                <button
                  type="button"
                  onClick={() => toggleCat(cat)}
                  data-testid={`tool-request-cat-${cat}`}
                  className="w-full flex items-center justify-between gap-2 px-4 py-2.5 bg-slate-50 border-b-2 border-slate-200 hover:bg-slate-100 transition-colors"
                >
                  <div className="flex items-center gap-2 min-w-0">
                    <ChevronDown
                      className={`h-4 w-4 text-slate-500 transition-transform ${open ? "" : "-rotate-90"}`}
                    />
                    <div className="font-bold uppercase tracking-wider text-xs text-slate-900 truncate">
                      {cat}
                    </div>
                  </div>
                  <div className="flex items-center gap-1.5 flex-shrink-0">
                    {catSelected > 0 && (
                      <span className="bg-emerald-700 text-white text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-sm border-2 border-emerald-900">
                        {catSelected} / {items.length}
                      </span>
                    )}
                    <span className="text-[10px] text-slate-500 font-bold tracking-wider uppercase">
                      {items.length}
                    </span>
                  </div>
                </button>
                {open && (
                  <ul className="divide-y divide-slate-100">
                    {items.map((t) => {
                      const checked = selected.has(t.id);
                      return (
                        <li
                          key={t.id}
                          className={`flex items-center gap-3 px-4 py-3 cursor-pointer transition-colors ${
                            checked ? "bg-emerald-50" : "hover:bg-slate-50"
                          }`}
                          onClick={() => toggle(t.id)}
                          data-testid={`tool-request-row-${t.id}`}
                        >
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={() => toggle(t.id)}
                            onClick={(e) => e.stopPropagation()}
                            data-testid={`tool-request-check-${t.id}`}
                            className="h-5 w-5 flex-shrink-0 accent-emerald-700 cursor-pointer"
                          />
                          <div className="flex-1 min-w-0">
                            <div className="font-bold text-sm text-slate-900 truncate">
                              {t.Tool_Name}
                            </div>
                            {t.Notes && (
                              <div className="text-[11px] text-slate-500 truncate">
                                {t.Notes}
                              </div>
                            )}
                          </div>
                          {checked && (
                            <span
                              data-testid={`tool-request-catid-${t.id}`}
                              className="font-mono font-bold text-xs bg-slate-900 text-white px-2 py-1 rounded-sm border-2 border-slate-700 flex-shrink-0"
                              title={`Cat ID: ${t.Cat_ID}`}
                            >
                              {t.Cat_ID}
                            </span>
                          )}
                          {!checked && (
                            <span className="font-mono text-[11px] text-slate-400 flex-shrink-0">
                              {t.Cat_ID}
                            </span>
                          )}
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Notes */}
      <div className="rg-card p-4 mt-4">
        <label className="block text-[11px] font-bold uppercase tracking-wider text-slate-700 mb-1.5">
          Requestor notes (optional)
        </label>
        <textarea
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          rows={3}
          maxLength={2000}
          data-testid="tool-request-notes"
          placeholder="Anything the supervisor should know — e.g. ‘Need for Tuesday morning install’"
          className="w-full border-2 border-slate-300 rounded-sm px-3 py-2 text-base bg-white focus:border-emerald-700 outline-none resize-y"
        />
      </div>

      {/* Selected summary */}
      {selected.size > 0 && (
        <div className="rg-card p-4 mt-4 border-2 border-emerald-300 bg-emerald-50" data-testid="tool-request-summary">
          <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-emerald-700 mb-2">
            Selected tools ({selected.size})
          </div>
          <ul className="space-y-1.5">
            {selectedTools.map((t) => (
              <li key={t.id} className="flex items-center justify-between gap-2 text-sm">
                <span className="font-bold text-slate-900 truncate">{t.Tool_Name}</span>
                <span className="font-mono font-bold text-xs bg-slate-900 text-white px-2 py-0.5 rounded-sm flex-shrink-0">
                  {t.Cat_ID}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Bottom CTA bar */}
      <div className="fixed bottom-0 left-0 right-0 z-30 bg-white border-t-2 border-slate-300 p-4 flex items-center gap-3">
        <div className="flex-1 text-xs">
          {selected.size === 0 ? (
            <span className="text-slate-500">Select at least one tool to continue.</span>
          ) : (
            <span className="text-slate-700">
              Ready — <strong>{selected.size}</strong> tool{selected.size === 1 ? "" : "s"} selected.
            </span>
          )}
        </div>
        <button
          type="button"
          onClick={onGenerate}
          disabled={!canSubmit}
          data-testid="tool-request-generate-btn"
          className="rg-tap-xl bg-emerald-700 hover:bg-emerald-800 disabled:opacity-50 disabled:cursor-not-allowed text-white font-display uppercase tracking-wider rounded-sm flex items-center gap-2 px-5 sm:px-7 min-h-[60px] text-base border-2 border-emerald-900"
        >
          {submitting ? (
            <>
              <Loader2 className="h-5 w-5 animate-spin" />
              Submitting…
            </>
          ) : (
            <>
              <FileText strokeWidth={2.5} className="h-5 w-5" />
              Generate Approval Letter
            </>
          )}
        </button>
      </div>
    </div>
  );
}
